'use client';

import { c as _c } from "react/compiler-runtime";
import { type TransitionStartFunction, useContext, useEffect, useRef } from 'react';
import { useAnswerDispatch, useIsAnswerHydrated, useSubmittedAnswers } from '@/entities/answer/model/hooks';
import { useSessionSync } from '@/entities/session/model/sessionSyncContext';
import { canReachStep } from '@/shared/config/funnel/branch';
import type { BranchId } from '@/shared/config/funnel/constants';
import type { FlattenedFunnelConfig } from '@/shared/config/funnel/flattened/types';
import { ENTRY_STEP_ID } from '@/shared/config/funnel/flowConstants';
import { clearPendingBranchSwitch, getPendingBranchSwitch } from '../pendingBranchSwitch';
import { resolveLandingAction } from '../resolveLandingAction';
import type { FunnelStepSnapshot } from '../stepSnapshot';
import { FunnelContext } from '.';
function useFunnelContext() {
  const ctx = useContext(FunnelContext);
  if (!ctx) {
    throw new Error("Funnel context hooks must be used within FunnelProvider");
  }
  return ctx;
}
export function useFunnelFlattenedConfig() {
  return useFunnelContext().funnelFlattenedConfig;
}
export function useFunnelStepId() {
  return useFunnelContext().stepId;
}
export function useBranchId() {
  return useFunnelContext().branchId;
}
export function useFunnelConfigVersion() {
  return useFunnelContext().funnelConfigVersion;
}
export function useIsBranchFirstStep() {
  return useFunnelContext().isBranchFirstStep;
}
export function useIsBranchLastStep() {
  return useFunnelContext().isBranchLastStep;
}
export function useIsFirstStep() {
  return useFunnelContext().isFirstStep;
}
export function useIsFunnelSubmitting() {
  return useFunnelContext().isSubmitting;
}
export function useStartSubmitTransition() {
  return useFunnelContext().startSubmitTransition;
}
export function useStepProgress() {
  return useFunnelContext().progress;
}

// Validates rendered step is reachable based on the stored answers
// If not, user gets redirected to the entry step
// Also corrects the branch according to the path param
export function useHandleLanding(t0) {
  const $ = _c(11);
  const {
    branchId,
    funnelFlattenedConfig,
    stepSnapshot
  } = t0;
  const {
    stepId,
    isFirstStep,
    isBranchFirstStep
  } = stepSnapshot;
  const isHydrated = useIsAnswerHydrated();
  const submittedAnswers = useSubmittedAnswers();
  const dispatch = useAnswerDispatch();
  const {
    abandonRun
  } = useSessionSync();
  const hasRedirectedRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== abandonRun || $[1] !== branchId || $[2] !== dispatch || $[3] !== funnelFlattenedConfig || $[4] !== isBranchFirstStep || $[5] !== isFirstStep || $[6] !== isHydrated || $[7] !== stepId || $[8] !== submittedAnswers) {
    t1 = () => {
      const branchFromAnswers = submittedAnswers[ENTRY_STEP_ID];
      if (branchId && typeof branchFromAnswers === "string" && branchId === branchFromAnswers) {
        clearPendingBranchSwitch();
      }
      const action = resolveLandingAction({
        branchId,
        isHydrated,
        isFirstStep,
        isBranchFirstStep,
        branchFromAnswers,
        pendingBranchSwitch: getPendingBranchSwitch(),
        canReachCurrentStep: branchId !== null && canReachStep(funnelFlattenedConfig, branchId, stepId, submittedAnswers),
        hasRedirected: hasRedirectedRef.current
      });
      switch (action.kind) {
        case "none":
          {
            return;
          }
        case "reset-branch":
          {
            dispatch({
              type: "RESET_BRANCH",
              branchId: action.branchId
            });
            return;
          }
        case "clear-redirect-guard":
          {
            hasRedirectedRef.current = false;
            return;
          }
        case "redirect-to-entry":
          {
            hasRedirectedRef.current = true;
            clearPendingBranchSwitch();
            abandonRun();
            return;
          }
      }
    };
    t2 = [branchId, isHydrated, isFirstStep, isBranchFirstStep, submittedAnswers, funnelFlattenedConfig, stepId, dispatch, abandonRun];
    $[0] = abandonRun;
    $[1] = branchId;
    $[2] = dispatch;
    $[3] = funnelFlattenedConfig;
    $[4] = isBranchFirstStep;
    $[5] = isFirstStep;
    $[6] = isHydrated;
    $[7] = stepId;
    $[8] = submittedAnswers;
    $[9] = t1;
    $[10] = t2;
  } else {
    t1 = $[9];
    t2 = $[10];
  }
  useEffect(t1, t2);
}